import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  useShopItems,
  usePurchaseItem,
  useCurrencyBalance,
  usePurchases,
} from "@/hooks/use-currency";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Coins, 
  Gem, 
  Store, 
  AlertCircle, 
  ShoppingCart, 
  Play, 
  Pause,
  Check,
  Music,
  ImageIcon,
  Search,
  Filter,
  Flag,
  StickyNote,
  X,
  ArrowUpDown,
  Star,
  Rocket,
  ShoppingBag,
  User,
  LayoutDashboard,
  Package,
  Settings
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CurrencyDisplay } from "./CurrencyDisplay";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ShopItem, PurchaseWithItem } from "@/hooks/use-currency";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth"; // Add useAuth hook

export function Shop() {
  const { toast } = useToast();
  const [_, setLocation] = useLocation();
  const { data: items, isLoading, isError } = useShopItems();
  const { data: balance } = useCurrencyBalance();
  const { data: userPurchases } = usePurchases();
  const { user } = useAuth(); // Add useAuth hook
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [sortOption, setSortOption] = useState<string>("default");
  const [showOwned, setShowOwned] = useState<boolean>(true);
  const [shopItems, setShopItems] = useState<ShopItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<ShopItem[]>([]);

  useEffect(() => {
    console.log('Shop items data:', items);
    console.log('Balance data:', balance);
    console.log('Loading state:', isLoading);
    console.log('Error state:', isError);
  }, [items, balance, isLoading, isError]);
  
  // Debug our condition for disabling the purchase button
  useEffect(() => {
    if (selectedItem && balance) {
      const insufficientFunds = 
        (selectedItem.currencyType === "regular" && selectedItem.price > (balance.currency || 0)) ||
        (selectedItem.currencyType === "premium" && selectedItem.price > (balance.premiumCurrency || 0));
      
      console.log('Selected item:', selectedItem);
      console.log('Current balance:', balance);
      console.log('Item price:', selectedItem.price);
      console.log('Currency type:', selectedItem.currencyType);
      console.log('User has insufficient funds:', insufficientFunds);
    }
  }, [selectedItem, balance]);
  
  // Stop audio playback when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);
  
  // Play or pause audio preview
  const toggleAudioPreview = (item: ShopItem, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering card click/purchase
    
    // Stop current audio if it's playing
    if (audioRef.current) {
      audioRef.current.pause();
      
      // If we're clicking the same item that's already playing, just stop it
      if (playingAudio === item.id) {
        setPlayingAudio(null);
        return;
      }
    }
    
    // Create new audio element if we don't have one
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    
    // Set the new source and play
    if (item.itemType === 'sound') {
      // Always prioritize audioUrl if available, fallback to imageUrl or provide a default
      // This ensures sound items always have a playable source
      const audioSource = item.audioUrl || item.imageUrl || `/sounds/${item.name.replace(/\s+/g, '-').toLowerCase()}.mp3`;
      
      console.log(`Attempting to play sound from: ${audioSource} for item ${item.id} (${item.name})`);
      
      if (!audioSource) {
        toast({
          title: "No Preview Available",
          description: "This sound doesn't have an audio file for preview",
          variant: "destructive"
        });
        return;
      }
      
      console.log(`Playing audio from: ${audioSource}`);
      
      try {
        audioRef.current.src = audioSource;
        audioRef.current.volume = 0.5;
        audioRef.current.onended = () => setPlayingAudio(null);
        
        // Add loading indicator logic
        setPlayingAudio(-1); // -1 indicates loading state
        
        audioRef.current.play()
          .then(() => {
            console.log(`Audio playback started for item ${item.id}`);
            setPlayingAudio(item.id);
          })
          .catch(err => {
            console.error('Error playing audio:', err);
            setPlayingAudio(null);
            
            // More detailed error message
            let errorMessage = "Failed to play audio preview";
            if (err.name === 'NotAllowedError') {
              errorMessage = "Browser blocked autoplay. Please interact with the page first.";
            } else if (err.name === 'NotSupportedError') {
              errorMessage = "Audio format not supported by your browser.";
            } else if (err.name === 'AbortError') {
              errorMessage = "Audio playback was aborted.";
            }
            
            toast({
              title: "Audio Error",
              description: errorMessage,
              variant: "destructive"
            });
          });
      } catch (err) {
        console.error('Error setting up audio:', err);
        setPlayingAudio(null);
        
        toast({
          title: "Audio Error",
          description: "Failed to set up audio player",
          variant: "destructive"
        });
      }
    }
  };
  
  // Check if user has already purchased an item
  const isItemPurchased = (item: ShopItem): boolean => {
    if (!userPurchases || userPurchases.length === 0) {
      console.log("No user purchases found or length is 0");
      return false;
    }
    
    console.log("Checking if item is purchased:", item.id, item.name);
    console.log("User purchases:", userPurchases);
    
    // Check for purchased items using the standard data structure
    const isPurchased = userPurchases.some((purchase: PurchaseWithItem) => 
      purchase.purchase.itemId === item.id || 
      (purchase.item && purchase.item.id === item.id)
    );
    
    console.log("Item purchased status:", isPurchased);
    return isPurchased;
  };
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { mutate: purchaseItem, isPending } = usePurchaseItem();

  // Extract categories from items for filter
  const getCategories = () => {
    if (!items || items.length === 0) return [];
    
    const categories = new Set<string>();
    
    if (Array.isArray(items)) {
      items.forEach(item => {
        if (item.itemType) {
          categories.add(item.itemType);
        }
        // Add subcategories if they exist
        if (item.itemType === 'sound' && item.soundType) {
          categories.add(`sound:${item.soundType}`);
        }
      });
    }
    
    return Array.from(categories);
  };

  // Debug: log raw items from API
  useEffect(() => {
    if (items) {
      console.log("Raw shop items from API:", items);
      console.log("Raw shop items type:", typeof items);
      console.log("Is Array?", Array.isArray(items));
      
      // Log some additional info about sound items
      if (Array.isArray(items)) {
        const soundItems = items.filter(item => item.itemType === 'sound');
        console.log("Number of sound items:", soundItems.length);
        if (soundItems.length > 0) {
          console.log("Sample sound item:", soundItems[0]);
          console.log("Sound item has audioUrl:", !!soundItems[0].audioUrl);
          console.log("Sound item has imageUrl:", !!soundItems[0].imageUrl);
          console.log("Sound item has soundType:", !!soundItems[0].soundType);
          console.log("Sound item type is:", soundItems[0].itemType);
        }
        
        console.log("Items is array with length:", items.length);
        setShopItems(items);
      } else if (typeof items === 'object') {
        // Convert from object to array if it's not already an array
        const itemsArray = Object.values(items) as ShopItem[];
        console.log("Converting object to array with length:", itemsArray.length);
        
        // Check for sound items in object format
        const soundItems = itemsArray.filter(item => item.itemType === 'sound');
        console.log("Number of sound items (object format):", soundItems.length);
        if (soundItems.length > 0) {
          console.log("Sample sound item (object format):", soundItems[0]);
        }
        
        setShopItems(itemsArray);
      } else {
        console.error("Unexpected items format:", items);
        setShopItems([]);
      }
    } else {
      console.log("Items is falsy:", items);
      setShopItems([]);
    }
  }, [items]);

  // Apply filters and search
  useEffect(() => {
    if (!shopItems || shopItems.length === 0) {
      setFilteredItems([]);
      return;
    }

    let result = [...shopItems];

    // Filter out manager mode items for non-managers
    result = result.filter(item => user?.role === 'manager' || user?.role === 'admin' || !item.managerMode);

    // Apply category filter
    if (selectedCategory !== "all") {
      if (selectedCategory.includes(':')) {
        const [mainType, subType] = selectedCategory.split(':');
        result = result.filter(item => 
          item.itemType === mainType && item.soundType === subType
        );
      } else {
        result = result.filter(item => item.itemType === selectedCategory);
      }
    }

    // Apply search term and showOwned filters
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(lowercasedSearch) || 
        (item.description && item.description.toLowerCase().includes(lowercasedSearch))
      );
    }

    if (!showOwned) {
      result = result.filter(item => !isItemPurchased(item));
    }

    // Apply existing sorting logic
    switch (sortOption) {
      case "price-asc":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        result.sort((a, b) => b.price - a.price);
        break;
      case "name-asc":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "newest":
        // Assuming newer items have higher IDs
        result.sort((a, b) => b.id - a.id);
        break;
      default:
        // Default sort - prioritize certain item types
        result.sort((a, b) => {
          // Sort by item type (place certain item types first)
          if (a.itemType !== b.itemType) {
            const typeOrder = { banner: 0, sound: 1, emote: 2 };
            return (typeOrder[a.itemType as keyof typeof typeOrder] || 99) - 
                   (typeOrder[b.itemType as keyof typeof typeOrder] || 99);
          }
          // Then by price (higher price first)
          return b.price - a.price;
        });
    }

    setFilteredItems(result);
  }, [shopItems, selectedCategory, searchTerm, sortOption, showOwned, userPurchases, user]);

  const handlePurchase = (item: ShopItem) => {
    // Don't allow purchase if already owned
    if (isItemPurchased(item)) {
      toast({
        title: "Already Purchased",
        description: "You already own this item",
      });
      return;
    }
    
    setSelectedItem(item);
    setConfirmDialogOpen(true);
    
    // Debug selected item and check if user can afford it
    console.log('Purchase initiated for item:', item);
    console.log('Current user balance:', balance);
    
    if (balance) {
      const canAfford = item.currencyType === 'regular' 
        ? balance.currency >= item.price
        : balance.premiumCurrency >= item.price;
      
      console.log('Can user afford this item?', canAfford);
      console.log('Item price:', item.price);
      console.log('Relevant balance:', item.currencyType === 'regular' 
        ? balance.currency 
        : balance.premiumCurrency);
    }
  };

  const confirmPurchase = () => {
    if (!selectedItem) return;
    
    // Additional check for already purchased (in case item was purchased in another tab/window)
    if (isItemPurchased(selectedItem)) {
      toast({
        title: "Already Purchased",
        description: "You already own this item",
      });
      setConfirmDialogOpen(false);
      return;
    }

    purchaseItem(selectedItem.id, {
      onSuccess: () => {
        toast({
          title: "Purchase successful!",
          description: `You've purchased ${selectedItem.name}.`,
        });
        setConfirmDialogOpen(false);
        
        // Play a success sound for audio items
        if (selectedItem.itemType === 'sound') {
          // Always prioritize audioUrl if available, fallback to imageUrl or provide a default
          const audioSource = selectedItem.audioUrl || selectedItem.imageUrl || `/sounds/${selectedItem.name.replace(/\s+/g, '-').toLowerCase()}.mp3`;
          
          if (audioSource) {
            console.log(`Playing purchase success sound from: ${audioSource}`);
            const audio = new Audio(audioSource);
            audio.volume = 0.6; // Slightly louder for purchase confirmation
            audio.play()
              .then(() => console.log('Purchase success sound played'))
              .catch(err => console.error('Error playing purchase success sound:', err));
          } else {
            console.error('No audio source available for purchase sound');
          }
        }
      },
      onError: (error: any) => {
        console.error("Purchase error details:", error);
        
        let errorMessage = "Failed to complete purchase.";
        
        // Try to extract error message from different possible formats
        if (error.message) {
          errorMessage = error.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        // Common error messages with better user-friendly text
        if (errorMessage.includes("Insufficient funds")) {
          errorMessage = "You don't have enough currency to make this purchase.";
        } else if (errorMessage.includes("out of stock")) {
          errorMessage = "This item is currently out of stock.";
        } else if (errorMessage.includes("already owns")) {
          errorMessage = "You already own this item.";
        }
        
        toast({
          title: "Purchase failed",
          description: errorMessage,
          variant: "destructive",
        });
      },
    });
  };

  // Handle click on board item
  const handleItemClick = (item: ShopItem, e: React.MouseEvent) => {
    // Only navigate for board types
    if (item.itemType === 'board') {
      e.stopPropagation(); // Prevent purchase confirmation from triggering
      setLocation(`/board/${item.id}`);
    }
  };

  // Direct rendering from API data
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center py-2">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            <h2 className="text-xl font-semibold">Shop</h2>
          </div>
          <CurrencyDisplay variant="icon" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="border border-gray-700">
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-3/4 bg-gray-700" />
              </CardHeader>
              <CardContent className="pb-2">
                <Skeleton className="h-24 w-full bg-gray-700" />
                <div className="flex justify-between mt-2">
                  <Skeleton className="h-4 w-16 bg-gray-700" />
                  <Skeleton className="h-4 w-16 bg-gray-700" />
                </div>
              </CardContent>
              <CardFooter>
                <Skeleton className="h-9 w-full bg-gray-700" />
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md bg-red-900/20 border border-red-700 p-4 text-red-200 text-sm flex items-center gap-2">
        <AlertCircle className="h-4 w-4" />
        <span>Failed to load shop items</span>
      </div>
    );
  }

  if (!shopItems || shopItems.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center py-2">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            <h2 className="text-xl font-semibold">Shop</h2>
          </div>
          <CurrencyDisplay variant="icon" />
        </div>
        <div className="text-center p-12 border border-gray-700 rounded-md bg-gray-800/50">
          <Store className="h-12 w-12 mx-auto mb-4 text-gray-500" />
          <p className="text-gray-400">No items available in the shop</p>
        </div>
      </div>
    );
  }

  // Get unique categories
  const categories = getCategories();
  
  // Improved rendering with categorization and filtering
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center py-2">
        <div className="flex items-center gap-2">
          <Store className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Item Shop</h2>
        </div>
        <CurrencyDisplay variant="icon" />
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search items..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-gray-800 border-gray-700 focus:border-primary h-9"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-white" />
              </button>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-24 bg-gray-800 border-gray-700 space-x-1 h-9">
                <Filter className="h-4 w-4" />
                <span>Filter</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-gray-800 border-gray-700">
              <DropdownMenuItem 
                onClick={() => setShowOwned(!showOwned)} 
                className="flex items-center justify-between"
              >
                <span>Show Purchased Items</span>
                <div className={`w-4 h-4 rounded-sm border ${showOwned ? 'bg-primary border-primary' : 'border-gray-500'} flex items-center justify-center`}>
                  {showOwned && <Check className="h-3 w-3 text-white" />}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-24 bg-gray-800 border-gray-700 space-x-1 h-9">
                <ArrowUpDown className="h-4 w-4" />
                <span>Sort</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-gray-800 border-gray-700">
              <DropdownMenuItem onClick={() => setSortOption("default")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "default" ? "text-primary" : ""}`}>
                  <span>Featured</span>
                  {sortOption === "default" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOption("price-asc")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "price-asc" ? "text-primary" : ""}`}>
                  <span>Price: Low to High</span>
                  {sortOption === "price-asc" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOption("price-desc")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "price-desc" ? "text-primary" : ""}`}>
                  <span>Price: High to Low</span>
                  {sortOption === "price-desc" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOption("name-asc")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "name-asc" ? "text-primary" : ""}`}>
                  <span>Name: A to Z</span>
                  {sortOption === "name-asc" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOption("newest")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "newest" ? "text-primary" : ""}`}>
                  <span>Newest</span>
                  {sortOption === "newest" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Category filter buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setSelectedCategory("all")}
            variant={selectedCategory === "all" ? "default" : "outline"}
            size="sm"
            className={`rounded-full h-8 ${selectedCategory === "all" ? "" : "bg-gray-800 border-gray-700"}`}
          >
            All Items
          </Button>
          
          <Button
            onClick={() => setSelectedCategory("avatar")}
            variant={selectedCategory === "avatar" ? "default" : "outline"}
            size="sm"
            className={`rounded-full h-8 flex items-center ${
              selectedCategory === "avatar" ? "" : "bg-gray-800 border-gray-700"
            }`}
          >
            <User className="h-3 w-3 mr-1" />
            Avatar
          </Button>
          
          <Button
            onClick={() => setSelectedCategory("banner")}
            variant={selectedCategory === "banner" ? "default" : "outline"}
            size="sm"
            className={`rounded-full h-8 flex items-center ${
              selectedCategory === "banner" ? "" : "bg-gray-800 border-gray-700"
            }`}
          >
            <Flag className="h-3 w-3 mr-1" />
            Banner
          </Button>
          
          <Button
            onClick={() => setSelectedCategory("sound")}
            variant={selectedCategory === "sound" ? "default" : "outline"}
            size="sm"
            className={`rounded-full h-8 flex items-center ${
              selectedCategory === "sound" ? "" : "bg-gray-800 border-gray-700"
            }`}
          >
            <Music className="h-3 w-3 mr-1" />
            Sound
          </Button>
          
          <Button
            onClick={() => setSelectedCategory("board")}
            variant={selectedCategory === "board" ? "default" : "outline"}
            size="sm"
            className={`rounded-full h-8 flex items-center ${
              selectedCategory === "board" ? "" : "bg-gray-800 border-gray-700"
            }`}
          >
            <LayoutDashboard className="h-3 w-3 mr-1" />
            Board
          </Button>
          
          <Button
            onClick={() => setSelectedCategory("booster")}
            variant={selectedCategory === "booster" ? "default" : "outline"}
            size="sm"
            className={`rounded-full h-8 flex items-center ${
              selectedCategory === "booster" ? "" : "bg-gray-800 border-gray-700"
            }`}
          >
            <Package className="h-3 w-3 mr-1" />
            Booster
          </Button>
          
          {/* Dynamic generation of other category buttons */}
          {categories
            .filter(cat => !['avatar', 'banner', 'booster', 'sound', 'board'].includes(cat) && !cat.includes(':'))
            .sort()
            .map((category) => {
              let icon;
              let displayName = category.charAt(0).toUpperCase() + category.slice(1);
              
              if (category === 'emote') {
                icon = <StickyNote className="h-3 w-3 mr-1" />;
              } else {
                icon = <Package className="h-3 w-3 mr-1" />;
              }
              
              return (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  className={`rounded-full h-8 flex items-center ${
                    selectedCategory === category ? "" : "bg-gray-800 border-gray-700"
                  }`}
                >
                  {icon}
                  {displayName}
                </Button>
              );
            })}
        </div>
      </div>

      {/* Filter results summary */}
      {(searchTerm || selectedCategory !== "all" || !showOwned) && (
        <div className="text-xs text-gray-400">
          Showing {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} 
          {searchTerm ? ` matching "${searchTerm}"` : ''}
          {selectedCategory !== "all" ? ` in ${selectedCategory}` : ''}
          {!showOwned ? ' (hiding owned items)' : ''}
        </div>
      )}

      {/* Selected items counter */}
      <div className="text-sm text-gray-400">
        {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} {searchTerm ? `matching "${searchTerm}"` : ''}
      </div>

      {/* Item grid - more compact with smaller card heights */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {filteredItems.map((item) => (
          <Card key={item.id} className="border border-gray-700 hover:border-gray-500 transition-all" onClick={(e) => handleItemClick(item, e)}>
            <CardHeader className="p-3 pb-2">
              <CardTitle className="text-white text-sm">{item.name}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 pb-2">
              {/* Always show the image container for all items, with a placeholder if no image */}
              <div className="w-full h-24 bg-gray-800 rounded-md mb-2 overflow-hidden relative">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {item.itemType === 'sound' ? (
                      <Music className="h-8 w-8 text-gray-500" />
                    ) : item.itemType === 'banner' ? (
                      <Flag className="h-8 w-8 text-gray-500" />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-gray-500" />
                    )}
                  </div>
                )}

                {/* Manager Mode badge */}
                {item.managerMode && (
                  <Badge className="absolute top-1 right-1 bg-purple-600/40 text-purple-300 text-xs rounded-sm font-medium">
                    Manager
                  </Badge>
                )}

                {/* Sound preview button */}
                {item.itemType === 'sound' && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="absolute bottom-1 right-1 h-6 w-6 p-0 rounded-full bg-black/60 hover:bg-black/80"
                    onClick={(e) => toggleAudioPreview(item, e)}
                    title="Preview sound"
                  >
                    {playingAudio === -1 ? (
                      // Loading indicator when audio is being loaded
                      <div className="h-3 w-3 rounded-full border-2 border-t-transparent border-white animate-spin" />
                    ) : playingAudio === item.id ? (
                      // Pause button when this item is playing
                      <Pause className="h-3 w-3 text-white" />
                    ) : (
                      // Play button when not playing
                      <Play className="h-3 w-3 text-white" />
                    )}
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-white">{item.price}</span>
                  {item.currencyType === "premium" ? (
                    <Gem className="h-4 w-4 text-purple-400" />
                  ) : (
                    <Coins className="h-4 w-4 text-amber-400" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Show purchased status */}
                  {isItemPurchased(item) && (
                    <Badge className="bg-green-700 text-green-100 text-xs px-1 py-0 h-5">
                      <Check className="h-3 w-3" />
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
            <CardFooter className="p-3 pt-0">
              {isItemPurchased(item) ? (
                <Button
                  className="w-full border-green-500/30 bg-green-950 hover:bg-green-900 text-green-400 text-xs h-8"
                  disabled={true}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Owned
                </Button>
              ) : (
                <Button
                  className={`w-full h-8 text-xs ${
                    balance && (
                      (item.currencyType === "regular" && item.price > balance.currency) ||
                      (item.currencyType === "premium" && item.price > balance.premiumCurrency)
                    ) ? "opacity-70" : ""
                  }`}
                  onClick={() => handlePurchase(item)}
                  disabled={
                    (balance ? (
                      (item.currencyType === "regular" && item.price > balance.currency) ||
                      (item.currencyType === "premium" && item.price > balance.premiumCurrency)
                    ) : false)
                  }
                  variant={
                    (balance && (
                      (item.currencyType === "regular" && item.price > balance.currency) ||
                      (item.currencyType === "premium" && item.price > balance.premiumCurrency)
                    )) ? "destructive" : "default"
                  }
                >
                  <ShoppingCart className="h-3 w-3 mr-1" />
                  Purchase
                </Button>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center p-8 border border-gray-700 rounded-md bg-gray-800/50 my-4">
          <Search className="h-8 w-8 mx-auto mb-2 text-gray-500" />
          <p className="text-gray-400">No items match your search</p>
        </div>
      )}

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="bg-gray-900 border border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Purchase</DialogTitle>
            <DialogDescription className="text-gray-300">
              Are you sure you want to purchase this item?
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <div className="py-4">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-white">
                  {selectedItem.name}
                </span>
                <div className="flex items-center gap-1 text-white">
                  <span>{selectedItem.price}</span>
                  {selectedItem.currencyType === "premium" ? (
                    <Gem className="h-4 w-4 text-purple-400" />
                  ) : (
                    <Coins className="h-4 w-4 text-amber-400" />
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                {selectedItem.description}
              </p>
              
              {/* Show current balance */}
              <div className="text-sm text-gray-400 bg-gray-800 p-3 rounded-md">
                <div className="flex justify-between mb-1">
                  <span>Current balance:</span>
                  <div className="flex items-center gap-1">
                    <span>{balance?.currency || 0}</span>
                    <Coins className="h-3 w-3 text-amber-400" />
                  </div>
                </div>
                <div className="flex justify-between">
                  <span>Premium balance:</span>
                  <div className="flex items-center gap-1">
                    <span>{balance?.premiumCurrency || 0}</span>
                    <Gem className="h-3 w-3 text-purple-400" />
                  </div>
                </div>
                {selectedItem && balance && 
                  ((selectedItem.currencyType === "regular" && selectedItem.price > balance.currency) ||
                  (selectedItem.currencyType === "premium" && selectedItem.price > balance.premiumCurrency)) && (
                  <div className="mt-2 bg-red-900/20 p-2 rounded-md border border-red-700">
                    <div className="flex items-center gap-2 text-red-400 font-medium">
                      <AlertCircle className="h-4 w-4" />
                      <span>Insufficient Funds</span>
                    </div>
                    <p className="text-red-400 text-xs mt-1">
                      You don't have enough {selectedItem.currencyType === "premium" ? "premium currency" : "currency"} for this purchase.
                      {selectedItem.currencyType === "regular" ? 
                        ` You need ${selectedItem.price - balance.currency} more coins.` : 
                        ` You need ${selectedItem.price - balance.premiumCurrency} more premium currency.`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setConfirmDialogOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmPurchase} 
              disabled={
                isPending || 
                !selectedItem || !balance || 
                (selectedItem.currencyType === "regular" && selectedItem.price > balance.currency) ||
                (selectedItem.currencyType === "premium" && selectedItem.price > balance.premiumCurrency)
              }
              variant={
                !selectedItem || !balance ? "default" :
                ((selectedItem.currencyType === "regular" && selectedItem.price > balance.currency) ||
                 (selectedItem.currencyType === "premium" && selectedItem.price > balance.premiumCurrency))
                  ? "destructive" : "default"
              }
              className={
                !selectedItem || !balance ? "" :
                ((selectedItem.currencyType === "regular" && selectedItem.price > balance.currency) ||
                 (selectedItem.currencyType === "premium" && selectedItem.price > balance.premiumCurrency))
                  ? "opacity-60 cursor-not-allowed" : ""
              }
            >
              {isPending ? (
                "Processing..."
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {!selectedItem || !balance ? "Confirm Purchase" :
                    ((selectedItem.currencyType === "regular" && selectedItem.price > balance.currency) ||
                     (selectedItem.currencyType === "premium" && selectedItem.price > balance.premiumCurrency))
                      ? "Insufficient Funds" : "Confirm Purchase"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}